/* Grievance Categories — routing, SLA and confidentiality configuration.

   This is the most consequential screen in the module: a category decides who
   can read a complaint, how fast they must answer, and whether the record can
   be filed anonymously. The API restricts writes here to org admins for that
   reason, and the UI mirrors it. */
import { useState } from "react";
import { Select, Switch } from "antd";
import { AlertTriangle, ListTree, Pencil, ShieldAlert, Trash2 } from "lucide-react";

import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { Reactable } from "../../components/ui/reactable";
import { Button } from "../../components/ui/button";
import { ConfirmPopupModal, DialogModal } from "../../components/ui/dialogModal";
import { modalDescriptionFieldProps, modalFieldLabelClass, modalTextareaClass } from "../../components/ui/fieldStyles";
import { Input, Textarea } from "../../components/ui/input";
import ToastMsg from "../../components/common/ToastMsg";
import { checkUserPermissionFunc } from "../../components/common/CommonFunctions";
import useApiMethods from "../../lib/useApiMethods";
import { EMPLOYEE_PERSONAL_INFO, GRIEVANCE_CATEGORIES } from "../../constants/api";
import { GRIEVANCE_PRIORITIES } from "../../constants/grievance";
import { antSelectClass } from "../../components/ui/fieldStyles";
import { Field, PriorityBadge } from "./GrievancePieces";

const SELECT_CLASS = antSelectClass();

const EMPTY = {
    name: "",
    description: "",
    default_priority: "medium",
    sla_ack_hours: 24,
    sla_resolution_days: 7,
    handler_employee: null,
    escalation_employee: null,
    is_sensitive: false,
    allow_anonymous: true,
    reopen_window_days: 7,
};

const GrievanceCategories = () => {
    const { fetch, create, update, remove } = useApiMethods();

    const isAdmin = JSON.parse(sessionStorage.getItem("is_admin") || "false");
    const isSuperAdmin = JSON.parse(sessionStorage.getItem("is_super_admin") || "false");
    const { add: addPermission, edit: editPermission } = checkUserPermissionFunc(
        "grievance", "Grievance Categories", "SubMenu",
    );
    /* Category configuration is org-admin only on the server (it controls who
       can read sensitive complaints), so the buttons follow the same rule
       rather than the looser permission flags. */
    const canConfigure = isAdmin || isSuperAdmin;

    const [rows, setRows] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [serverTrigger, setServerTrigger] = useState(false);
    const [employees, setEmployees] = useState([]);
    const [form, setForm] = useState(null); // null = closed, object = open
    const [editId, setEditId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(null);

    const refresh = () => setServerTrigger((t) => !t);
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const loadEmployees = async () => {
        if (employees.length) return;
        try {
            const res = await fetch(EMPLOYEE_PERSONAL_INFO, { drop_down: "True" });
            if (res?.status === "success") setEmployees(res.data || []);
        } catch (e) {
            ToastMsg("error", e.message);
        }
    };

    const openAdd = async () => {
        await loadEmployees();
        setEditId(null);
        setForm({ ...EMPTY });
    };

    const openEdit = async (row) => {
        await loadEmployees();
        setEditId(row.id);
        setForm({
            name: row.name || "",
            description: row.description || "",
            default_priority: row.default_priority || "medium",
            sla_ack_hours: row.sla_ack_hours ?? 24,
            sla_resolution_days: row.sla_resolution_days ?? 7,
            handler_employee: row.handler_employee_id ?? null,
            escalation_employee: row.escalation_employee_id ?? null,
            is_sensitive: !!row.is_sensitive,
            allow_anonymous: row.allow_anonymous !== false,
            reopen_window_days: row.reopen_window_days ?? 7,
        });
    };

    const resetForm = () => { setForm(null); setEditId(null); };

    const save = async () => {
        if (!form.name.trim()) { ToastMsg("error", "Please enter a category name"); return; }
        setLoading(true);
        try {
            const payload = {
                ...form,
                name: form.name.trim(),
                sla_ack_hours: Number(form.sla_ack_hours) || 24,
                sla_resolution_days: Number(form.sla_resolution_days) || 7,
                reopen_window_days: Number(form.reopen_window_days) || 7,
            };
            const res = editId
                ? await update(GRIEVANCE_CATEGORIES, { id: editId, ...payload })
                : await create(GRIEVANCE_CATEGORIES, payload);

            if (res?.status === "success") {
                ToastMsg("success", res.message || "Category saved");
                resetForm();
                refresh();
            } else {
                ToastMsg("error", res?.message || "Could not save category");
            }
        } catch (e) {
            ToastMsg("error", e.message);
        } finally {
            setLoading(false);
        }
    };

    const doDelete = async () => {
        setLoading(true);
        try {
            const res = await remove(`${GRIEVANCE_CATEGORIES}?id=${confirmDelete.id}`);
            if (res?.status === "success") {
                ToastMsg("success", res.message || "Category removed");
                setConfirmDelete(null);
                refresh();
            } else {
                ToastMsg("error", res?.message || "Could not remove category");
            }
        } catch (e) {
            ToastMsg("error", e.message);
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            accessor: "name", Header: "Category", filterable: true, sortable: true,
            Cell: ({ row }) => (
                <div className="flex items-center gap-1.5">
                    <span className="font-medium">{row.name || "-"}</span>
                    {row.is_sensitive && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-semibold text-rose-700 dark:bg-rose-900/30 dark:text-rose-300">
                            <ShieldAlert className="h-3 w-3" /> Restricted
                        </span>
                    )}
                </div>
            ),
        },
        {
            accessor: "default_priority", Header: "Default priority",
            Cell: ({ value }) => <PriorityBadge priority={value} />,
        },
        {
            accessor: "sla_ack_hours", Header: "Ack SLA",
            Cell: ({ value }) => <span className="text-sm">{value ? `${value} hrs` : "-"}</span>,
        },
        {
            accessor: "sla_resolution_days", Header: "Resolution SLA",
            Cell: ({ value }) => <span className="text-sm">{value ? `${value} days` : "-"}</span>,
        },
        {
            accessor: "handler_name", Header: "Handler",
            Cell: ({ value }) => (
                value
                    ? <span className="text-sm">{value}</span>
                    : (
                        <span className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
                            <AlertTriangle className="h-3 w-3" /> Falls back to admins
                        </span>
                    )
            ),
        },
        { accessor: "escalation_name", Header: "Escalates to", Cell: ({ value }) => value ?? "-" },
        {
            accessor: "allow_anonymous", Header: "Anonymous",
            Cell: ({ value }) => (
                <span className="text-sm">{value ? "Allowed" : "Not allowed"}</span>
            ),
        },
        ...(canConfigure
            ? [{
                accessor: "Action", Header: "",
                Cell: ({ row }) => (
                    <div className="flex items-center gap-1">
                        <Button
                            variant="ghost" size="sm" className="text-sidebar-primary"
                            onClick={(e) => { e.stopPropagation(); openEdit(row); }}
                        >
                            <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                            variant="ghost" size="sm" className="text-destructive"
                            onClick={(e) => { e.stopPropagation(); setConfirmDelete(row); }}
                        >
                            <Trash2 className="h-4 w-4" />
                        </Button>
                    </div>
                ),
            }]
            : []),
    ];

    const employeeOptions = employees.map((e) => ({
        label: `${e.employee_name || ""} (${e.employee_id || ""})`.trim(),
        value: e.id,
    }));

    return (
        <ContainerCard scrollable>
            <ModuleTitleCard
                Icon={ListTree}
                title="Grievance Categories"
                subtitle="Route each type of complaint to a handler, with its own SLA and confidentiality rules"
                totalRecords={totalRecords}
                buttonEnable={canConfigure && (addPermission || isAdmin || isSuperAdmin)}
                buttonName="Add Category"
                setOpenDialog={openAdd}
            />

            <Reactable
                columns={columns}
                data={rows}
                setData={setRows}
                setTotalCount={setTotalRecords}
                url={GRIEVANCE_CATEGORIES}
                serverSide
                serverTrigger={serverTrigger}
                isCSVRequired
                isPDFRequired
                tableHeight="calc(100vh - 330px)"
                rowSize="sm"
                onRowClick={(row) => (canConfigure && editPermission !== false ? openEdit(row) : undefined)}
            />

            {form && (
                <DialogModal
                    open
                    onClose={resetForm}
                    onSave={save}
                    title={editId ? "Edit Grievance Category" : "Add Grievance Category"}
                    saveLabel={editId ? "Save Changes" : "Create Category"}
                    editMode={!!editId}
                    loading={loading}
                    width="72rem"
                    maxWidth="96vw"
                >
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-start">
                        <div className="space-y-4 min-w-0">
                            <Field label="Category name" required>
                                <Input
                                    value={form.name}
                                    onChange={(e) => set("name", e.target.value)}
                                    placeholder="e.g. Harassment / POSH"
                                    maxLength={100}
                                    disabled={loading}
                                />
                            </Field>

                            <Field label="Description" hint="Shown to employees when they pick this category.">
                                <Textarea
                                    value={form.description}
                                    onChange={(e) => set("description", e.target.value)}
                                    disabled={loading}
                                    {...modalDescriptionFieldProps}
                                    className={modalTextareaClass}
                                />
                            </Field>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <Field label="Default priority" required>
                                    <Select
                                        className={SELECT_CLASS}
                                        value={form.default_priority}
                                        onChange={(v) => set("default_priority", v)}
                                        options={GRIEVANCE_PRIORITIES.map((p) => ({ label: p.label, value: p.value }))}
                                        disabled={loading}
                                    />
                                </Field>
                                <Field label="Ack SLA (hours)" required>
                                    <Input
                                        type="number"
                                        min={1}
                                        value={form.sla_ack_hours}
                                        onChange={(e) => set("sla_ack_hours", e.target.value)}
                                        disabled={loading}
                                    />
                                </Field>
                                <Field label="Resolve SLA (days)" required>
                                    <Input
                                        type="number"
                                        min={1}
                                        value={form.sla_resolution_days}
                                        onChange={(e) => set("sla_resolution_days", e.target.value)}
                                        disabled={loading}
                                    />
                                </Field>
                                <Field
                                    label="Reopen window (days)"
                                    hint="How long the employee has to reject the resolution."
                                >
                                    <Input
                                        type="number"
                                        min={1}
                                        value={form.reopen_window_days}
                                        onChange={(e) => set("reopen_window_days", e.target.value)}
                                        disabled={loading}
                                    />
                                </Field>
                            </div>
                        </div>

                        <div className="space-y-4 min-w-0">
                            <Field
                                label="Handler (level 1)"
                                hint="Leave empty to route to every org admin."
                            >
                                <Select
                                    className={SELECT_CLASS}
                                    allowClear
                                    showSearch
                                    optionFilterProp="label"
                                    placeholder="Select an employee"
                                    value={form.handler_employee}
                                    onChange={(v) => set("handler_employee", v ?? null)}
                                    options={employeeOptions}
                                    disabled={loading}
                                />
                            </Field>
                            <Field
                                label="Escalation contact (level 2)"
                                hint="Receives the grievance on SLA breach. Must differ from the handler."
                            >
                                <Select
                                    className={SELECT_CLASS}
                                    allowClear
                                    showSearch
                                    optionFilterProp="label"
                                    placeholder="Select an employee"
                                    value={form.escalation_employee}
                                    onChange={(v) => set("escalation_employee", v ?? null)}
                                    options={employeeOptions}
                                    disabled={loading}
                                />
                            </Field>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-stretch">
                                <label className="flex h-full min-h-[7.5rem] items-start justify-between gap-3 rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                                    <span className="min-w-0 flex-1">
                                        <span className="block text-sm font-medium text-gray-800 dark:text-gray-200">
                                            Restricted category
                                        </span>
                                        <span className="block text-xs text-muted-foreground mt-1 leading-relaxed">
                                            Only handler, escalation contact and org admins can see these grievances.
                                        </span>
                                    </span>
                                    <Switch
                                        className="shrink-0"
                                        checked={form.is_sensitive}
                                        onChange={(v) => set("is_sensitive", v)}
                                        disabled={loading}
                                    />
                                </label>

                                <label className="flex h-full min-h-[7.5rem] items-start justify-between gap-3 rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                                    <span className="min-w-0 flex-1">
                                        <span className="block text-sm font-medium text-gray-800 dark:text-gray-200">
                                            Allow anonymous filing
                                        </span>
                                        <span className="block text-xs text-muted-foreground mt-1 leading-relaxed">
                                            The raiser&apos;s name is hidden from everyone handling the grievance.
                                        </span>
                                    </span>
                                    <Switch
                                        className="shrink-0"
                                        checked={form.allow_anonymous}
                                        onChange={(v) => set("allow_anonymous", v)}
                                        disabled={loading}
                                    />
                                </label>
                            </div>

                            {form.is_sensitive && !form.handler_employee && (
                                <div className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-900/40 dark:bg-amber-900/20">
                                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-300" />
                                    <p className="text-xs text-amber-800 dark:text-amber-200 leading-relaxed">
                                        A restricted category with no handler falls back to every org
                                        admin. Name a handler to narrow the audience.
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </DialogModal>
            )}

            {confirmDelete && (
                <ConfirmPopupModal
                    open
                    type="danger"
                    destructive
                    title="Remove this category?"
                    description={`"${confirmDelete.name}" will be deactivated. Grievances already closed under it keep pointing at it for reporting. Categories with open grievances cannot be removed.`}
                    onClose={() => setConfirmDelete(null)}
                    onConfirm={doDelete}
                    confirmLabel="Remove"
                    loading={loading}
                />
            )}
        </ContainerCard>
    );
};

export default GrievanceCategories;
